import React, {
	createContext,
	ReactNode,
	useState,
	useContext,
	useEffect,
} from 'react';
import { getConfig } from './services';
import { useAuth } from 'hooks/useAuth';
import { loggerInfo } from 'utils/infoLog';
import { useToast } from '@welbe/web-design-system';

type DefaultType = { [key: string]: { enabled: boolean } };

interface IFeatureFlagProviderProps {
	children: ReactNode;
}

export interface IFeatureFlagProvider {
	features: DefaultType | null;
	isLoadingFeatures: boolean;
	handleFeatures: (value: DefaultType) => void;
	fetchFeaturesConfig: () => void;
}

export const FeatureFlagContext = createContext({
	features: null,
	isLoadingFeatures: false,
	handleFeatures: () => null,
	fetchFeaturesConfig: () => null,
} as IFeatureFlagProvider);

export const FeatureFlagProvider = ({
	children,
}: IFeatureFlagProviderProps) => {
	const { isAuthenticated } = useAuth();
	const { toast } = useToast();
	const [isLoadingFeatures, setLoadingFeatures] = useState<boolean>(true);
	const [features, setFeatures] = useState<DefaultType | null>(null);

	const handleFeatures = (features: any) => {
		setFeatures({ ...features });
	};

	const fetchFeaturesConfig = async () => {
		try {
			setLoadingFeatures(true);
			const { data } = await getConfig();
			if (data.content) {
				setFeatures({ ...data.content });
			}
		} catch (err) {
			loggerInfo({
				data: err,
				context: 'access',
				location: 'FeatureFlagProvider',
			});
			toast.error('Error al obtener las configuraciones de las features');
		} finally {
			setLoadingFeatures(false);
		}
	};

	useEffect(() => {
		if (isAuthenticated) {
			fetchFeaturesConfig();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated]);

	return (
		<FeatureFlagContext.Provider
			value={{
				features,
				isLoadingFeatures,
				handleFeatures,
				fetchFeaturesConfig,
			}}
		>
			{children}
		</FeatureFlagContext.Provider>
	);
};

export const useFeatureFlag = () => useContext(FeatureFlagContext);
